import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HdmuiMainApplicationHeaderBreadcrumb } from '@heidelberg/hdmui-angular';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRouteDataService } from '@data-terminal/data-access/activated-route-data/activated-route-data.service';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs/operators';
import { getParams } from '@data-terminal/utils/helpers';
import { ROUTE_PARAMS } from '@data-terminal/shared-models/navigation';

@Injectable({
    providedIn: 'root',
})
export class BreadcrumbsService {
    private readonly translateService = inject(TranslateService);
    private readonly activatedRouteDataService = inject(ActivatedRouteDataService);
    private readonly activatedRoute = inject(ActivatedRoute);

    private readonly _routeParamsMap = new Map<string, string>();
    private readonly _breadcrumbs$: BehaviorSubject<HdmuiMainApplicationHeaderBreadcrumb[]> = new BehaviorSubject<
        HdmuiMainApplicationHeaderBreadcrumb[]
    >([]);
    public breadcrumbs$ = this._breadcrumbs$.asObservable();

    constructor() {
        this.breadcrumbs$ = this.activatedRouteDataService.activatedRouteData$.pipe(
            map((data) => {
                const breadcrumbsCopy = this.createCopy(data.breadcrumbs);
                return this.createCloudToolbarBreadcrumbs(breadcrumbsCopy, getParams(this.activatedRoute));
            })
        );
    }

    public hasParam(key: string): boolean {
        return this._routeParamsMap.has(key);
    }

    public getParam(key: string): string | undefined {
        return this._routeParamsMap.get(key);
    }

    public saveParam(key: string, value: string): void {
        this._routeParamsMap.set(key, value);
    }

    private createCopy(
        breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[] | undefined
    ): HdmuiMainApplicationHeaderBreadcrumb[] {
        if (!breadcrumbs) {
            return [];
        }
        return breadcrumbs.map((breadcrumb) => ({
            text: breadcrumb.text,
            routerLink: Array.isArray(breadcrumb.routerLink) ? [...breadcrumb.routerLink] : breadcrumb.routerLink,
        }));
    }

    private createCloudToolbarBreadcrumbs(
        breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[],
        params: { [key: string]: string }
    ): HdmuiMainApplicationHeaderBreadcrumb[] {
        breadcrumbs = this.replaceBreadcrumbsText(breadcrumbs);
        return this.replaceRouteParams(breadcrumbs, params);
    }

    private replaceBreadcrumbsText(
        breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[]
    ): HdmuiMainApplicationHeaderBreadcrumb[] {
        this.replaceDynamicName(breadcrumbs, ROUTE_PARAMS.machineId);
        this.replaceDynamicName(breadcrumbs, ROUTE_PARAMS.primaryKey);
        this.translateBreadcrumbText(breadcrumbs);
        return breadcrumbs;
    }

    private replaceDynamicName(breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[], routeParam: string): void {
        const breadcrumb = breadcrumbs.find((e) => e.text === routeParam);
        if (breadcrumb) {
            breadcrumb.text = this._routeParamsMap.get(routeParam) || breadcrumb.text;
        }
    }

    private translateBreadcrumbText(breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[]): void {
        breadcrumbs.forEach((el) => (el.text = this.translateService.instant(el.text)));
    }

    private replaceRouteParams(
        breadcrumbs: HdmuiMainApplicationHeaderBreadcrumb[],
        params: { [key: string]: string }
    ): HdmuiMainApplicationHeaderBreadcrumb[] {
        return breadcrumbs.map((breadcrumb) => {
            const routerLink = breadcrumb.routerLink;
            if (!Array.isArray(routerLink) || routerLink.length === 0) {
                return { ...breadcrumb, routerLink: undefined };
            }
            return {
                ...breadcrumb,
                routerLink: routerLink.map((segment) => params[segment] ?? segment),
            };
        });
    }
}
